import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { SiteDashboardService } from '../../site-dashboard.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs/internal/Subscription';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import { SitesService } from 'src/app/sites/sites.service';

@Component({
	selector: 'app-site-energy-consumption',
	templateUrl: './site-energy-consumption.component.html',
	styleUrls: ['./site-energy-consumption.component.css'],
})

export class SiteEnergyConsumptionComponent implements OnInit, OnDestroy, OnChanges {

	@Input() title: string = 'iotemeter_site_energy_report.site_energy_consumption';
	@Input() device: any;
	@Input() tooltips: string = '';

	currentSite: any = {};
	siteSubscription: Subscription = new Subscription();
	utilizationSettingsSubscription: Subscription = new Subscription();

	readonly circumference = 2 * Math.PI * 65; // circle radius 65

	readonly allYearsId = 'All-Years';
	readonly allQuartersId = 'All Quarters';
	readonly allMonthsId = 'All-Months';
	readonly allDevicesId = 'All-Devices';

	readonly periods = { MONTHLY: 'monthly', QUARTERLY: 'quarterly' };
	readonly periodTypes = [
		{ label: this.translate.instant('g.quarterly'), value: this.periods.QUARTERLY },
		{ label: this.translate.instant('g.monthly'), value: this.periods.MONTHLY },
	];

	installationDate = null;

	yearList: any[] = [];
	quarterList = [];
	monthList = [];
	readonly devicesList = [
		{ label: this.translate.instant('iotemeter_site_energy_report.all_meters'), value: this.allDevicesId },
		{ label: this.translate.instant('iotemeter_site_energy_report.edge'), value: DeviceFactory.types.ioTEmeterEdge },
		{ label: this.translate.instant('iotemeter_site_energy_report.pro'), value: DeviceFactory.types.ioTEmeter },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp3'), value: DeviceFactory.types.ioTEmeterSp3 },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp1'), value: DeviceFactory.types.ioTEmeterSp1 },
	];

	selectedYear: string | number;
	selectedPeriodType: string = this.periods.MONTHLY;
	selectedQuarter: string = '';
	selectedMonth: string | number = '';
	selectedDeviceType: string | number = this.allDevicesId;

	responseData: any = {};
	data: any = {
		consumed_energy: 0,
		average_consumed_energy: 0,
		peak_energy_demand: 0,

		energy_cost: 0,
		demand_charges: 0,
		total_cost: 0,

		energy_cost_percent: 0,
		demand_charges_percent: 0,
	};

	costChartConfig = {
		titleKey: 'utilization.cost_breakdown',
	};

	siteEnergyUtilizationSettings: any = {};

	constructor(
		private sitesService: SitesService,
		public siteDashboardService: SiteDashboardService,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private siteService: SitesService,
	) { }

	initializeData() {
		if (this.device?.config_info?.installation_date)
			this.installationDate = moment.utc(this.device.config_info.installation_date * 1000).startOf('day').toDate();

		const { yearList, currentYear } = this.siteDashboardService.prepareYearsList(this.yearList, this.installationDate);
		this.yearList = [{ label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId }, ...yearList];
		this.selectedYear = currentYear;
		this.selectedPeriodType = this.periods.MONTHLY;

		this.setListQuarters();
		this.setListMonths();

		this.getSiteUtilizationSettings();
	}

	ngOnInit() {
		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.currentSite = siteInfo;
				this.installationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
				this.initializeData();
			}
		});
	}

	ngOnChanges(changes) {
		if (changes.device && !changes.device.firstChange) {
			this.device = changes.device.currentValue;
			this.initializeData();
		}
	}

	getSiteUtilizationSettings() {
		this.siteService.getSiteUtilizationSettings(this.currentSite.id).subscribe(res => {
			this.siteEnergyUtilizationSettings = res[0].energy_utilization_settings || {};
			this.getIotemeterEnergyConsumption();
		});
	}

	selectYear() {
		this.setListQuarters();
		this.setListMonths();
		this.getIotemeterEnergyConsumption();
	}

	selectPeriodType() {
		this.setListQuarters();
		this.setListMonths();
		this.getIotemeterEnergyConsumption();
	}

	setListQuarters() {
		const minDate = this.installationDate;

		let quarters: any[] = [this.allQuartersId];

		if (+this.selectedYear === moment().year() || +this.selectedYear === moment(minDate).year()) {
			for (let quarter = 1; quarter <= 4; quarter++) {
				if ((+this.selectedYear === moment().year() && quarter > moment().quarter()) || (quarter < moment(minDate).quarter() && +this.selectedYear === moment(minDate).year()))
					continue;

				quarters.push(`Q${quarter}`);
			}

			this.selectedQuarter = +this.selectedYear === moment().year() ? `Q${moment().format('Q')}` : quarters[1];
			return this.quarterList = quarters;
		}

		quarters = [this.allQuartersId, 'Q1', 'Q2', 'Q3', 'Q4']

		this.selectedQuarter = quarters[1];
		this.quarterList = quarters;
	}

	setListMonths() {
		const months: any[] = [{ value: this.allMonthsId, label: this.translate.instant('g.all_months') }];
		const installationYear = moment(this.installationDate).year();
		const installationMonth = moment(this.installationDate).month(); // 0-indexed
		const startMonth = +this.selectedYear === installationYear ? installationMonth : 0;

		if (+this.selectedYear === moment().year()) {
			for (let month = startMonth; month <= moment().month(); month++) {
				months.push({ value: month + 1, label: moment().month(month).format('MMMM') });
			}

			const currentMonthInList = (months.filter(month => month.label == moment().format('MMMM')))[0].value;

			this.selectedMonth = structuredClone(currentMonthInList);
			this.monthList = months;
			return months;
		}

		for (let i = startMonth; i < 12; i++) {
			months.push({ value: i + 1, label: moment().month(i).format('MMMM') });
		}

		this.monthList = months;
		this.selectedMonth = months[1].value;

		return months;
	}

	isAllYearsSelected() {
		return this.selectedYear === this.allYearsId;
	}

	getIotemeterEnergyConsumption() {
		const criteria: any = {
			siteId: this.currentSite.id,
			year: this.selectedYear,
			periodType: this.selectedPeriodType,
			quarter: this.allQuartersId,
			month: this.allMonthsId,
			deviceType: this.selectedDeviceType,
		};

		if (this.device)
			criteria.mac_address = this.device.mac_address;

		this.sitesService.getIotemeterEnergyConsumption(criteria).subscribe((response: { summary: any }) => {
			this.responseData = response.summary;
			this.prepareData();
		});
	}

	prepareData() {
		let data;

		switch (true) {
			// all years
			case this.isAllYearsSelected():
				data = this.responseData?.[this.allYearsId];
				break;
			// all auarters
			case this.selectedPeriodType == this.periods.QUARTERLY:
				if (this.selectedQuarter == this.allQuartersId)
					data = this.responseData?.[this.allQuartersId];
				else {
					const quarterNumber = this.selectedQuarter.split('Q')[1];
					data = this.responseData?.quarters?.[quarterNumber];
				}
				break;
			case this.selectedPeriodType == this.periods.MONTHLY:
				if (this.selectedMonth == this.allMonthsId)
					data = this.responseData?.[this.allMonthsId];
				else
					data = this.responseData?.months?.[this.selectedMonth];
				break;
		}

		this.data.consumed_energy = data?.consumed_energy || 0;
		this.data.average_consumed_energy = data?.average_consumed_energy || 0;
		this.data.peak_energy_demand = data?.peak_energy_demand || 0;

		this.calculateCost();
	}

	calculateCost() {
		this.data.energy_cost = (this.data.consumed_energy * (this.siteEnergyUtilizationSettings.energy_cost || 0)) / 100;
		this.data.demand_charges = this.data.peak_energy_demand * (this.siteEnergyUtilizationSettings.energy_demand_charge || 0);
		this.data.total_cost = (this.data.energy_cost + this.data.demand_charges);

		this.data.energy_cost_percent = +((this.data.energy_cost / this.data.total_cost) * 100 || 0).toFixed(0);
		this.data.demand_charges_percent = +((this.data.demand_charges / this.data.total_cost) * 100 || 0).toFixed(0);

		this.data.hasData = this.data.consumed_energy > 0 || this.data.peak_energy_demand > 0;

		this.data.consumed_energy = this.data.consumed_energy.toLocaleString();
		this.data.average_consumed_energy = this.data.average_consumed_energy.toLocaleString();
		this.data.peak_energy_demand = this.data.peak_energy_demand.toLocaleString();
		this.data.energy_cost = this.data.energy_cost.toLocaleString();
		this.data.demand_charges = this.data.demand_charges.toLocaleString();
		this.data.total_cost = this.data.total_cost.toLocaleString();
	}

	getEnergyDashArray(): string {
		const filledDash = this.circumference * ((this.data?.energy_cost_percent || 0) / 100);
		const gap = this.circumference - filledDash;
		return `${filledDash} ${gap}`;
	}

	getDemandDashArray(): string {
		const filledDash = this.circumference * ((this.data?.demand_charges_percent || 0) / 100);
		const gap = this.circumference - filledDash;
		return `${filledDash} ${gap}`;
	}

	getDemandDashOffset(): number {
		return -(this.circumference * ((this.data?.energy_cost_percent || 0) / 100));
	}

	getCostBreakdownPercentage() {
		if (this.data.demand_charges_percent || this.data.energy_cost_percent)
			return `%100`;
		return '%0';
	}

	ngOnDestroy() {
		this.siteSubscription.unsubscribe();
		this.utilizationSettingsSubscription.unsubscribe();
	}
}
